import { useSeo } from '../lib/seo';
/**
 * /half-days — curated 3-hour itinerary loops. Each card links to a stable
 * shareable URL `/half-days/<slug>`.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowUpRight, Clock, MapPin, Sparkles } from 'lucide-react';
import api from '../lib/api';

const MOOD_COLORS = {
  family: 'bg-mustard/20 text-charcoal',
  outdoors: 'bg-forest/15 text-forest',
  heritage: 'bg-heritage/15 text-heritage',
  foodie: 'bg-mustard/30 text-charcoal',
  'rainy-day': 'bg-sky/30 text-charcoal',
  romantic: 'bg-heritage/15 text-heritage',
};

export default function HalfDaysPage() {
  useSeo(
    'Half-Day Itineraries in Leicestershire | Go Leicestershire',
    'Short on time? Ready-made half-day Leicestershire itineraries pairing great venues, walks and pit-stops.'
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/half-day-itineraries')
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="half-days-page">
      <Helmet>
        <title>Half-day itineraries · GoLeicestershire</title>
        <meta name="description" content="Curated 3-hour itineraries across Leicestershire — pick a corner of the county and we'll plan the loop. Built for families with toddlers, busy weekends, or anyone short on time." />
      </Helmet>

      <div className="label text-heritage mb-3 inline-flex items-center gap-2"><Sparkles size={14}/> Half-days</div>
      <h1 className="font-display text-5xl lg:text-7xl leading-[0.95] mb-4">
        Hand-curated loops, <em className="not-italic text-heritage">ready when you are.</em>
      </h1>
      <p className="text-muted text-lg max-w-2xl mb-12">
        Eight planned mini-itineraries across the county — heritage runs, forest afternoons, indoor rescues for the rainy days.
        Each is anchored to a real postcode so the route makes sense. Open any to see the timings and tweak from there.
      </p>

      {loading && <div className="text-center py-20 text-muted">Loading…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {items.map((it, i) => (
          <Link
            key={it.slug}
            to={`/half-days/${it.slug}`}
            data-testid={`half-day-card-${it.slug}`}
            className={`group relative bg-white border border-charcoal/10 rounded-3xl p-7 md:p-8 hover:shadow-soft hover:-translate-y-1 transition-all overflow-hidden ${i === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
          >
            <div className="flex items-center justify-between mb-5">
              <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full font-semibold ${MOOD_COLORS[it.mood] || 'bg-stone text-charcoal/70'}`}>
                {it.mood?.replace('-', ' ') || 'mixed'}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-muted">
                <Clock size={12}/> Curated loop
              </span>
            </div>
            <h2 className="font-display text-3xl lg:text-4xl leading-tight mb-3 group-hover:text-heritage transition-colors">
              {it.title}
            </h2>
            <p className="text-charcoal/75 mb-5 leading-relaxed">{it.sub}</p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted inline-flex items-center gap-1.5"><MapPin size={12}/> {it.region_label}</span>
              <span className="text-heritage font-semibold text-sm inline-flex items-center gap-1">
                Plan this loop <ArrowUpRight size={13}/>
              </span>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-16 bg-charcoal text-base rounded-3xl p-8 md:p-12">
        <div className="label text-mustard mb-3">Need a full day?</div>
        <h2 className="font-display text-3xl md:text-4xl mb-3">Plan My Day builds a custom 8-hour loop from your postcode.</h2>
        <p className="text-base/80 mb-6 max-w-2xl">Pick a mood, pick a postcode, and we&apos;ll route an open-today, weather-aware day around the county for you.</p>
        <Link to="/plan" data-testid="half-days-cta-plan" className="bg-mustard text-charcoal px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-base">
          Plan my day <ArrowUpRight size={14}/>
        </Link>
      </div>
    </div>
  );
}
